import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export const Sidebar: React.FC = () => {
  const { user, logout } = useAuth();

  return (
    <aside className="w-64 bg-white/60 dark:bg-dark-200/60 backdrop-blur-md flex flex-col h-screen sticky top-0 border-r border-cream-300/50 dark:border-dark-400/50">
      {/* Header - Minimal & Clean */}
      <div className="px-6 pt-8 pb-6">
        <h1 className="text-2xl font-bold text-soft-black dark:text-white tracking-tight">
          Playnest
        </h1>
        <p className="text-sm text-soft-gray dark:text-gray-400 mt-1">
          Campaigns Admin
        </p>
      </div>

      {/* Navigation - Airbnb Style */}
      <nav className="flex-1 px-4 space-y-1">
        <NavLink
          to="/campaigns"
          className={({ isActive }) =>
            `flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
              isActive
                ? 'bg-warm-400 dark:bg-dark-purple-300 text-soft-black dark:text-white font-semibold shadow-soft'
                : 'text-soft-gray dark:text-gray-400 hover:bg-cream-200 dark:hover:bg-dark-300 font-normal'
            }`
          }
        >
          <svg
            className="w-5 h-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="text-sm">Campanhas</span>
        </NavLink>

        <NavLink
          to="/projects"
          className={({ isActive }) =>
            `flex items-center gap-4 px-4 py-3 rounded-2xl transition-all duration-300 ${
              isActive
                ? 'bg-warm-400 dark:bg-dark-purple-300 text-soft-black dark:text-white font-semibold shadow-soft'
                : 'text-soft-gray dark:text-gray-400 hover:bg-cream-200 dark:hover:bg-dark-300 font-normal'
            }`
          }
        >
          <svg
            className="w-5 h-5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.5"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <span className="text-sm">Projetos</span>
        </NavLink>

        {/* Locked Item - Subtle & Clean */}
        <div className="relative group">
          <div className="flex items-center gap-4 px-4 py-3 rounded-lg text-gray-300 dark:text-gray-700 cursor-not-allowed">
            <svg
              className="w-5 h-5"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-sm">Recrutamento</span>
            <svg
              className="w-3.5 h-3.5 ml-auto"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          {/* Tooltip on hover */}
          <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap pointer-events-none z-50">
            Em breve
            <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
          </div>
        </div>
      </nav>

      {/* Divider */}
      <div className="mx-6 border-t border-gray-100 dark:border-dark-400"></div>

      {/* User Profile & Logout - Airbnb Style */}
      <div className="p-4">
        <div className="flex items-center gap-3 px-2 py-3 mb-2">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-red-500 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm">
                {user?.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white dark:border-dark-200 rounded-full"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {user?.name}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
              {user?.email}
            </p>
          </div>
        </div>
        <button
          onClick={logout}
          className="w-full px-4 py-3 text-sm font-medium text-soft-gray dark:text-gray-300 hover:text-soft-black dark:hover:text-white transition-all duration-300 border-0 rounded-2xl hover:bg-cream-200 dark:hover:bg-dark-300 shadow-soft hover:shadow-soft-lg"
        >
          Sair da conta
        </button>
      </div>
    </aside>
  );
};
